import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useAppSelector } from 'hooks/redux.hooks';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Button } from '@mui/material';
import { Add } from '@mui/icons-material';
import _ from 'lodash';
import { CpDefaultSort, CpId, CpTableColumn, CpTableColumnTitles, ICpActive } from 'constants/cp.constants';
import { userStatuses } from 'constants/user.constants';
import { IColumnType } from 'components/Table/types';
import SelectBox from 'components/SelectBox';
import BasicTable from 'components/Table';
import { pagesEnum } from 'constants/ui.constants';
import CircularIndeterminate from 'components/Loading';
import NoInfo from 'components/NoInfo';
import { filterByFields } from '../../utils/global.util';
import { DataFetchKeys, useDataFetchContext } from '../../context/DataFetchContext';
import { PageContentAddNew, PageContentContainer, PageContentFilter, PageContentTable } from '../PageContentContainer';
import { useDebounce } from 'hooks/useDebounce';

const CpPage: React.FC = () => {
  const navigate = useNavigate();
  const { siteId } = useParams<{ siteId?: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { cps = [] } = useAppSelector((state) => state.cp);
  const { sites = [], loading, error } = useAppSelector((state) => state.sites);
  const [searchValue, setSearchValue] = useState<string>(searchParams.get('search') || '');
  const debouncedValue = useDebounce(searchValue);
  const [filterValues, setFilterValues] = useState<Record<string, any>>({});
  const { loadDataByKeys } = useDataFetchContext();

  const cpColumns = useMemo(() => {
    const columns: IColumnType[] = [];
    _.keys(CpTableColumn).forEach((element) => {
      columns.push({
        fieldId: CpId,
        name: element,
        title: CpTableColumnTitles[element as keyof typeof CpTableColumnTitles] || element,
        type: CpTableColumn[element],
        customValueHandle: (val: any, rec: any) => (rec.active ? ICpActive.Active : ICpActive.Inactive)
      });
    });
    return columns;
  }, []);

  const parsedSitesOptions = useMemo(() => {
    if (!cps?.length) return [];
    return _.uniq(cps?.map((cp) => cp.assignedSite ?? 'No site'));
  }, [cps]);

  const parsedCountriesOptions = useMemo(() => {
    if (!cps?.length || !sites?.length) return [];
    const cpsSiteNames = new Set(cps.map((cp) => cp.assignedSite));
    return _.uniq(
      sites.map((site) => (site.id && site.name && cpsSiteNames.has(site.name) ? site.country : null)).filter(Boolean)
    );
  }, [sites, cps]);

  const onFilterChange = (filterType: string, filterValue: string | string[]) => {
    if (siteId && filterType === 'sites') {
      navigate(`/${pagesEnum.CP}`, { replace: true });
    }

    const updatedFilterValues = {
      ...filterValues,
      [filterType]: { ...filterValues[filterType], value: filterValue }
    };
    setFilterValues(updatedFilterValues);

    const params = new URLSearchParams(searchParams);
    if (Array.isArray(filterValue)) {
      params.delete(filterType);
      filterValue.forEach((value) => value && params.append(filterType, value));
    } else {
      params.set(filterType, filterValue);
    }
    setSearchParams(params);
  };

  const onResetSelections = () => {
    const resetValues = _.mapValues(filterValues, (value, key) => ({
      ...value,
      value: ['country', 'sites'].includes(key) ? ['All'] : 'All'
    }));
    setFilterValues(resetValues);
    setSearchValue('');
    setSearchParams(new URLSearchParams());
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
  };

  useEffect(() => {
    if (!cps?.length) loadDataByKeys([DataFetchKeys.CPS]);
    if (!sites?.length) loadDataByKeys([DataFetchKeys.SITES]);
  }, [cps?.length, sites?.length, loadDataByKeys]);

  useEffect(() => {
    if (parsedSitesOptions.length && parsedCountriesOptions.length) {
      setFilterValues({
        sites: {
          displayName: 'Sites',
          options: parsedSitesOptions,
          value: searchParams.getAll('sites').length > 0 ? searchParams.getAll('sites') : ['All'],
          multiple: true
        },
        country: {
          displayName: 'Country',
          options: parsedCountriesOptions,
          value: searchParams.getAll('country').length > 0 ? searchParams.getAll('country') : ['All'],
          multiple: true
        },
        status: {
          displayName: 'Status',
          options: userStatuses,
          value: searchParams.get('status') || 'All'
        }
      });
    }
  }, [parsedSitesOptions, parsedCountriesOptions, searchParams]);

  const updateTableData = useCallback(() => {
    if (!cps || !filterValues?.country) return [];

    return cps
      .map((cp) => ({
        ...cp,
        assignedSite: cp.assignedSite === 'All' || !cp.assignedSite ? 'Unrestricted' : cp.assignedSite
      }))
      .filter((cp) => {
        const isStatusMatch =
          !filterValues.status.value ||
          filterValues.status.value === 'All' ||
          (filterValues.status.value === 'Active') === cp.active;

        const isCountryMatch =
          filterValues.country.value.includes('All') || filterValues.country.value.includes(cp.country);

        const isSiteMatch =
          filterValues.sites.value.includes('All') ||
          (filterValues.sites.value === 'No site'
            ? !cp.assignedSite
            : filterValues.sites.value.includes(cp.assignedSite));

        return isStatusMatch && isCountryMatch && isSiteMatch;
      });
  }, [cps, filterValues]);

  const filteredCpData = useMemo(() => {
    const filtered = updateTableData();
    return filterByFields(filtered, debouncedValue, ['idNumber', 'country', 'assignedSite']);
  }, [updateTableData, debouncedValue]);

  const actionsCells = useMemo(
    () => [
      {
        element: (
          <Button
            color='secondary'
            sx={{ marginRight: '46px', textTransform: 'none', fontWeight: 'bold' }}
            variant='text'
          >
            Info
          </Button>
        ),
        onClick: (cp) => navigate(`/${pagesEnum.CP}/${cp.id}`)
      }
    ],
    [navigate]
  );

  if (error) return <NoInfo />;
  if (loading) return <CircularIndeterminate />;

  return (
    <PageContentContainer
      pageName='CP'
      searchValue={searchValue}
      filteredResultsCount={filteredCpData?.length || 0}
      resetFilterClicked={onResetSelections}
      searchValueChanged={handleInputChange}
    >
      <PageContentAddNew>
        <Button
          sx={{ textTransform: 'none' }}
          color='secondary'
          variant='contained'
          startIcon={<Add />}
          onClick={() => navigate(`/${pagesEnum.CP}/new`)}
        >
          Add New CP
        </Button>
      </PageContentAddNew>
      <PageContentFilter>
        {Object.keys(filterValues).map((filterValuesKey) => {
          let filter = filterValues[filterValuesKey];
          if (filter?.displayName === 'Sites') {
            filter = { ...filter, options: [...filter.options, 'Unrestricted'] };
          }
          return (
            <SelectBox
              key={filterValuesKey}
              items={filter?.options || []}
              values={filter?.value || ''}
              label={filter?.displayName}
              inputLabel={filter?.displayName}
              type={filterValuesKey}
              onFilterChange={onFilterChange}
              withAllOption
              multiple={filter?.multiple}
            />
          );
        })}
      </PageContentFilter>
      <PageContentTable>
        <BasicTable
          fieldId='id'
          data={filteredCpData}
          columns={cpColumns}
          defaultSortBy={CpDefaultSort}
          actionsCell={actionsCells}
        />
      </PageContentTable>
    </PageContentContainer>
  );
};

export default CpPage;
